import React from 'react';
import classNames from 'classnames';
import styles from './MiniPlanOverviewSection.less';
import { Tooltip, TooltipPrimitiveLight } from '@trello/nachos/tooltip';
import {
  freeFeatureList,
  standardFeatureList,
  premiumFeatureList,
  FeatureListItem,
} from './getPlanFeatureStrings';
import { forTemplate } from '@trello/i18n';

const formatTemplate = forTemplate('mini_plan_comparison');

export const renderTooltip = (
  tooltip: string | null,
  target: React.ReactNode,
) =>
  tooltip ? (
    <Tooltip content={tooltip} component={TooltipPrimitiveLight}>
      {target}
    </Tooltip>
  ) : (
    target
  );

interface MiniPlanOverviewSectionProps {
  plan: 'free' | 'standard' | 'premium';
}

interface PlanOverviewDetails {
  list: FeatureListItem[];
  header: string;
  subtext?: string;
  subtextClassNames?: string;
  // TODO: remove containerClassNames after GTM as there will be no requirement for the individual plan boxes to have different styles
  containerClassNames: string;
}

const usePlanDetails = ({
  plan,
}: MiniPlanOverviewSectionProps): PlanOverviewDetails | undefined => {
  switch (plan) {
    case 'free':
      return {
        list: freeFeatureList,
        header: formatTemplate(['free', 'name']),
        containerClassNames: classNames(
          styles.freePlan,
          styles.freePlanRebrand,
        ),
      };
    case 'standard':
      return {
        list: standardFeatureList,
        header: formatTemplate(['standard', 'name']),
        containerClassNames: classNames(styles.staPlan, styles.staPlanRebrand),
      };
    case 'premium':
      return {
        list: premiumFeatureList,
        header: formatTemplate(['premium', 'name']),
        containerClassNames: styles.premiumPlan,
      };
    default:
      return;
  }
};

export const MiniPlanOverviewSection: React.FC<MiniPlanOverviewSectionProps> = ({
  plan,
}) => {
  const planDetails = usePlanDetails({
    plan,
  });

  if (!planDetails) return null;
  return (
    <div
      className={classNames(
        planDetails.containerClassNames,
        styles.planContainer,
      )}
    >
      <h2>{planDetails.header}</h2>
      {planDetails.subtext && (
        <h3 className={planDetails.subtextClassNames}>{planDetails.subtext}</h3>
      )}
      <ul>
        {planDetails.list.map(({ icon: Icon, content, tooltip }, index) => (
          <li
            key={index}
            className={classNames(
              styles.featureItem,
              !!tooltip && styles.tooltipTarget,
            )}
          >
            {renderTooltip(
              tooltip,
              <div className={styles.featureItemWrapper}>
                <Icon color="quiet" dangerous_className={styles.featureIcon} />
                <div className={styles.featureTextWrapper}>
                  <span className={styles.featureContent}>
                    {content.replace(/&amp;/g, '&')}
                  </span>
                </div>
              </div>,
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
